import { CheckCircleIcon } from "@heroicons/react/20/solid";
import cleanDeep from "clean-deep";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import apiURL from "../../../store/actions/apiURL";
import { Actions as COMPANYACTIONS } from "../../../store/actions/companyActions";
import { Actions as LEAVEACTIONS } from "../../../store/actions/leaveActions";
import LoadingContent from "../../common/LoadingContent";
import Spinner from "../../common/Spinner";
import UpgradeRequired from "../../company-pages/UpgradeRequired";
import Rule from "./Rule";

const TimeAndAttendanceSettings = ({
	currentOrg,
	updateCompanyDetails,
	organisationDetails,
	updateSetting,
	allowanceOvertimeOptions,
	isDemo,
	getLeaveTypes,
	disabled,
	includedInPlan,
}) => {
	useEffect(() => {
		getLeaveTypes();
	}, []);

	const {
		enableTimesheets,
		requirePhoto,
		requireLocation,
		enableMobileClockIn,
		excludePublicHolidaysFromTargetHours,
		employeeViewTimesheets,
		employeeInputTimesheets,
		managerViewTimesheets,
		managerInputTimesheets,
	} = organisationDetails;

	const { handleSubmit, control, register, watch } = useForm();

	const mobileClockIn = watch("enableMobileClockIn", enableMobileClockIn);

	const onSubmit = (data) => {
		if (saving) return;

		if (!mobileClockIn) {
			data.requireLocation = false;
			data.requirePhoto = false;
		}

		updateCompanyDetails(
			cleanDeep(
				{ org: String(currentOrg), ...data },
				{ emptyStrings: false, emptyArrays: false, emptyObjects: false },
			),
		);

		setSaving(true);
		fetch(apiURL + "/settings/timesheet-rules", {
			credentials: "include",
			method: "POST",
			body: JSON.stringify(rules),
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
			},
		})
			.then((response) => response.json())
			.then((data) => {
				// obtained data
				setSaving(false);
				console.log("saved settings", data);
			});
	};

	const [rules, setRules] = useState([]);

	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		console.log("starting to obtain Data");

		if (!loaded) {
			// set loaded right away to avoid a re-trigger

			const paths = ["/settings/timesheet-rules"];

			Promise.all(
				paths.map(
					(path) =>
						fetch(apiURL + path, {
							credentials: "include",
							method: "GET",
							headers: {
								"Accept": "application/json",
								"Content-Type": "application/json",
							},
						}).then((response) => response.json()),
					// .catch(logError)
				),
			).then((data) => {
				// do something with the data
				console.log("data recieved", data);

				setLoaded(true);

				setRules(data[0].rules || []);
			});
		}
	}, []);

	if (!loaded) return <LoadingContent />;
	if (!includedInPlan && !isDemo) return <UpgradeRequired />;

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="px-8">
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 form-container">
					<div className="pull-left">
						<h4>Timesheet Settings</h4>
					</div>
				</div>
				<div className="form-container col-xs-6">
					<label className="css-input switch switch-sm switch-success">
						<input
							id="enable-timesheets"
							disabled={disabled}
							ref={register}
							checked={enableTimesheets}
							type="checkbox"
							className="update-company-settings"
							name="enableTimesheets"
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Enable Timesheets
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							id="excludePublicHolidaysFromTargetHours"
							disabled={disabled}
							ref={register}
							checked={excludePublicHolidaysFromTargetHours}
							type="checkbox"
							className="update-company-settings"
							name="excludePublicHolidaysFromTargetHours"
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Exclude Public Holidays From Target Hours
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							id="show-timesheets-to-employees"
							disabled={disabled}
							ref={register}
							checked={employeeViewTimesheets}
							type="checkbox"
							className="update-company-settings"
							name="employeeViewTimesheets"
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Allow employees to view timesheets
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							id="allow-employees-to-input-timesheets"
							disabled={disabled}
							ref={register}
							checked={employeeInputTimesheets}
							type="checkbox"
							className="update-company-settings"
							name="employeeInputTimesheets"
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Allow employees to manually input timesheets
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							id="show-timesheets-to-managers"
							disabled={disabled}
							ref={register}
							checked={managerViewTimesheets}
							type="checkbox"
							className="update-company-settings"
							name="managerViewTimesheets"
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Allow managers to view timesheets
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							id="allow-managers-to-input-timesheets"
							disabled={disabled}
							ref={register}
							checked={managerInputTimesheets}
							type="checkbox"
							className="update-company-settings"
							name="managerInputTimesheets"
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Allow managers to manually input timesheets
					</label>
					<label className="css-input switch switch-sm switch-success">
						<input
							id="enableMobileClockIn"
							disabled={disabled}
							ref={register}
							checked={enableMobileClockIn}
							type="checkbox"
							className="update-company-settings"
							name="enableMobileClockIn"
							onChange={(e) => updateSetting(e.target.name, e.target.checked)}
						/>
						<span />
						Enable Mobile Clock In
					</label>
					{mobileClockIn ? (
						<>
							<div className="form-container col-xs-12">
								<label className="css-input switch switch-sm switch-success">
									<input
										id="requireLocation"
										disabled={disabled}
										ref={register}
										checked={requireLocation}
										type="checkbox"
										className="update-company-settings"
										name="requireLocation"
										onChange={(e) => updateSetting(e.target.name, e.target.checked)}
									/>
									<span />
									Require Location
								</label>
								<label className="css-input switch switch-sm switch-success">
									<input
										id="requirePhoto"
										disabled={disabled}
										ref={register}
										checked={requirePhoto}
										type="checkbox"
										className="update-company-settings"
										name="requirePhoto"
										onChange={(e) => updateSetting(e.target.name, e.target.checked)}
									/>
									<span />
									Require Photo
								</label>
							</div>
						</>
					) : null}
					<div className="clearfix"></div>
				</div>
			</div>
			{!disabled && (
				<button
					type="submit"
					className="btn btn-success pull-right"
				>
					{saving ? (
						<Spinner
							style={{ height: "1.5em", float: "left", marginRight: 10 }}
							className="animate-spin -ml-1 mr-3 h-5 w-5 "
						/>
					) : (
						<CheckCircleIcon style={{ height: "1.5em", float: "left", marginRight: 10 }} />
					)}
					<span>Save</span>
				</button>
			)}

			<div className="clearfix" />
		</form>
	);
};

const mapStateToProps = (state) => {
	const allowanceOvertimeOptions = [];

	state.company?.settings?.overtimes?.map((o) => {
		allowanceOvertimeOptions.push({
			value: o.id,
			label: o.name,
		});
	});
	state.company?.settings?.allowances?.map((o) => {
		allowanceOvertimeOptions.push({
			value: o.id,
			label: o.name,
		});
	});

	const leaveTypes = state.leave?.leaveTypes?.map((o) => {
		return {
			value: o.id,
			label: o.name,
		};
	});

	return {
		currentOrg: state.company?.settings?.org,
		allowanceOvertimeOptions,
		allowances: state.company?.settings?.allowances,
		overtime: state.company?.settings?.overtimes,
		departments: state.company?.settings?.departments,
		leaveTypes: leaveTypes,
		isDemo: state.company?.settings?.isDemo,
		organisationDetails: state.company?.settings,
		disabled:
			state?.permissions?.my?.permissions?.settings == "Read" || state?.company?.settings?.status == "Archived",
		jurisdiction: state.company?.settings?.jurisdiction,
		includedInPlan: state.permissions?.plan?.timesheets,
	};
};

const mapDispatchToProps = (dispatch) => ({
  updateSetting: (field, newValue) => dispatch(COMPANYACTIONS.updateSetting(field, newValue, false)),
  updateCompanyDetails: (body) => dispatch(COMPANYACTIONS.updateCompanyDetails(body)),
  getLeaveTypes: () => dispatch(LEAVEACTIONS.getLeaveTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TimeAndAttendanceSettings);
